// feature/notion-polish — toast system. Top-right portal, kind-colored accent stripe,
// hover-pauses auto-dismiss, manual close button. API is back-compat with
// the old useToast().push(msg, kind) used everywhere in the app.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { setToastPusher } from "./toastBridge";

const Ctx = createContext(null);

const DEFAULT_DURATION = {
  success: 4000,
  info:    4000,
  warning: 6000,
  error:   8000,
};

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const dismissTimers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setItems(arr => arr.filter(t => t.id !== id));
    const tm = dismissTimers.current.get(id);
    if (tm) { clearTimeout(tm); dismissTimers.current.delete(id); }
  }, []);

  const scheduleDismiss = useCallback((id, ms) => {
    const tm = setTimeout(() => dismiss(id), ms);
    dismissTimers.current.set(id, tm);
  }, [dismiss]);

  const push = useCallback((msg, kind = "success", subtext) => {
    const id = Math.random().toString(36).slice(2);
    const ms = DEFAULT_DURATION[kind] || DEFAULT_DURATION.success;
    setItems(arr => {
      const next = [...arr, { id, msg, kind, subtext, ms }];
      // Keep at most 4 visible — drop oldest beyond.
      return next.length > 4 ? next.slice(-4) : next;
    });
    scheduleDismiss(id, ms);
    return id;
  }, [scheduleDismiss]);

  useEffect(() => { setToastPusher(push); return () => setToastPusher(null); }, [push]);

  function pause(id) {
    const tm = dismissTimers.current.get(id);
    if (tm) { clearTimeout(tm); dismissTimers.current.delete(id); }
  }
  function resume(id, ms) { scheduleDismiss(id, ms); }

  return (
    <Ctx.Provider value={{ push, dismiss }}>
      {children}
      <div style={{
        position: "fixed", top: 20, right: 20, zIndex: 9000,
        display: "flex", flexDirection: "column", gap: 8,
        pointerEvents: "none",
      }}>
        {items.map(t => (
          <div
            key={t.id}
            className={"toast " + (t.kind || "")}
            style={{ pointerEvents: "auto" }}
            onMouseEnter={() => pause(t.id)}
            onMouseLeave={() => resume(t.id, Math.min(t.ms, 3000))}
          >
            <div style={{ flex: 1 }}>
              <div style={{ color: "var(--text-primary)" }}>{t.msg}</div>
              {t.subtext && (
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{t.subtext}</div>
              )}
            </div>
            <button className="x" onClick={() => dismiss(t.id)} aria-label="Cerrar">×</button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() { return useContext(Ctx); }
